import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { getAllPosts, getAllCategories, getPostsByCategory, formatDate } from '../../utils/blogUtils'
import { submitForm } from '../../services/formService'
import FloatingBackground from '../../components/FloatingBackground'
import Seo from '../../components/Seo'
import { FadeIn } from '../../components/Motion'

const CARD_COLORS = ['#3b82f6', '#8b5cf6', '#00008B']

function BlogCard({ post, color }) {
  return (
    <div className="relative group h-full">
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-xl translate-x-1.5 translate-y-1.5 transition-transform duration-200 group-hover:translate-x-2.5 group-hover:translate-y-2.5"
        style={{ backgroundColor: color }}
      />
      <Link
        to={`/blog/${post.slug}`}
        className="relative block h-full bg-white rounded-xl border-2 border-intelligence-900 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <span
              className="font-mono text-[10px] font-bold uppercase tracking-widest text-white px-2 py-0.5 rounded"
              style={{ backgroundColor: color }}
            >
              {post.category}
            </span>
            <span className="text-xs text-intelligence-400">{post.readTime}</span>
          </div>
          <h3 className="text-xl font-bold text-intelligence-900 mb-2">
            {post.title}
          </h3>
          <p className="text-intelligence-600 text-sm mb-4 line-clamp-2">
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <span className="text-sm text-intelligence-500">{formatDate(post.date)}</span>
            <span className="text-intelligence-900 font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Read more <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </div>
  )
}

function FeaturedPost({ post }) {
  return (
    <div className="relative group">
      <div
        aria-hidden="true"
        className="absolute inset-0 rounded-2xl bg-[#00008B] translate-x-2 translate-y-2 transition-transform duration-200 group-hover:translate-x-3 group-hover:translate-y-3"
      />
      <Link
        to={`/blog/${post.slug}`}
        className="relative block bg-intelligence-900 rounded-2xl border-2 border-intelligence-900 overflow-hidden"
      >
        <div className="p-8 md:p-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 mb-5 border-2 border-white bg-[#8b5cf6] text-white text-xs font-bold uppercase tracking-widest rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            Featured
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-white mt-2 mb-4 leading-tight">
            {post.title}
          </h2>
          <p className="text-intelligence-300 mb-6 max-w-2xl text-lg">
            {post.excerpt}
          </p>
          <div className="flex items-center text-intelligence-400 text-sm font-mono uppercase tracking-widest">
            <span>{post.author}</span>
            <span className="mx-2">·</span>
            <span>{formatDate(post.date)}</span>
            <span className="mx-2">·</span>
            <span>{post.readTime}</span>
          </div>
        </div>
      </Link>
    </div>
  )
}

export default function BlogIndex() {
  const [activeCategory, setActiveCategory] = useState('All')
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [newsletterStatus, setNewsletterStatus] = useState({ type: '', message: '' })
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false)

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault()
    setNewsletterSubmitting(true)
    setNewsletterStatus({ type: '', message: '' })
    const result = await submitForm({ type: 'newsletter', data: { email: newsletterEmail } })
    if (result.success) {
      setNewsletterStatus({ type: 'success', message: "You're subscribed!" })
      setNewsletterEmail('')
    } else {
      setNewsletterStatus({ type: 'error', message: result.error })
    }
    setNewsletterSubmitting(false)
  }

  const categories = getAllCategories()
  const allPosts = getAllPosts()
  const filteredPosts = getPostsByCategory(activeCategory)
  const featuredPost = allPosts.find((post) => post.featured)

  return (
    <div>
      <Seo
        title="Blog — Insights &amp; Resources"
        description="Thoughts on customer feedback, employee engagement, conversational surveys, and the future of research from the PrismScope team."
        path="/blog"
      />

      {/* Hero */}
      <section className="relative bg-white py-20 md:py-28 overflow-hidden">
        <FloatingBackground />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              The blog
            </p>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.95]">
              Insights &amp;{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B] bg-clip-text text-transparent">
                  resources
                </span>
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 bg-[#8b5cf6]/25 -z-0 -skew-y-1"
                />
              </span>
              <span className="text-[#00008B]">.</span>
            </h1>
            <p className="text-xl md:text-2xl text-intelligence-700 max-w-2xl mx-auto leading-relaxed">
              Thoughts on customer feedback, employee engagement, and the future of research.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="bg-intelligence-100 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <FeaturedPost post={featuredPost} />
            </FadeIn>
          </div>
        </section>
      )}

      {/* Category Filters */}
      <section className="bg-intelligence-100 pt-4 pb-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all ${
                    activeCategory === category
                      ? 'bg-intelligence-900 text-white border-intelligence-900 shadow-[2px_2px_0_0_#8b5cf6]'
                      : 'bg-white text-intelligence-900 border-intelligence-900 shadow-[2px_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_rgba(15,23,42,1)]'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="bg-intelligence-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post, i) => (
                <FadeIn key={post.slug} delay={i * 0.04}>
                  <BlogCard post={post} color={CARD_COLORS[i % CARD_COLORS.length]} />
                </FadeIn>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-intelligence-500">No posts found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter — chunky retro */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-2xl bg-[#3b82f6] translate-x-2 translate-y-2"
              />
              <div className="relative bg-white rounded-2xl border-2 border-intelligence-900 p-8 md:p-10 text-center">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-3">
                  Newsletter
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-intelligence-900 mb-2">
                  Get insights delivered
                </h2>
                <p className="text-intelligence-600 mb-6">
                  Weekly thoughts on customer feedback, employee engagement, and building products people love.
                </p>
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    required
                    placeholder="you@company.com"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="flex-1 px-4 py-3 border-2 border-intelligence-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8b5cf6]"
                  />
                  <button
                    type="submit"
                    disabled={newsletterSubmitting}
                    className="px-6 py-3 bg-intelligence-900 text-white border-2 border-intelligence-900 rounded-lg font-bold transition-transform hover:-translate-y-0.5 hover:shadow-[3px_3px_0_0_#8b5cf6] disabled:opacity-50"
                  >
                    {newsletterSubmitting ? 'Subscribing…' : 'Subscribe'}
                  </button>
                </form>
                {newsletterStatus.message ? (
                  <p
                    className={`text-xs mt-3 ${
                      newsletterStatus.type === 'success' ? 'text-prism-emerald' : 'text-prism-red'
                    }`}
                  >
                    {newsletterStatus.message}
                  </p>
                ) : (
                  <p className="text-intelligence-400 text-xs mt-3">
                    No spam. Unsubscribe anytime.
                  </p>
                )}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
